import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useSuperAdminDashboard } from '../../../hooks/useDashboard';
import { useAuditLogs } from '../../../hooks/useAuditLogs';
import { Card } from '../../../components/common/Card';
import { Icons } from '../../../components/common/Icons';

import { getGreeting, formatDateTime, getFirstName } from '../../../utils/helpers';
import { ROLE_FULL_NAMES } from '../../../utils/constants';
import type { AuditLogEntry } from '../../../types';
import './SuperAdminDashboard.css';

type TabKey = 'overview' | 'activity' | 'settings' | 'permissions';

const ACTION_COLORS: Record<string, { bg: string; color: string }> = {
  create: { bg: '#dcfce7', color: '#16a34a' },
  update: { bg: '#dbeafe', color: '#2563eb' },
  delete: { bg: '#fee2e2', color: '#dc2626' },
  login: { bg: '#f1f5f9', color: '#64748b' },
  logout: { bg: '#f1f5f9', color: '#64748b' },
  approve: { bg: '#dcfce7', color: '#16a34a' },
  reject: { bg: '#fee2e2', color: '#dc2626' },
  submit: { bg: '#dbeafe', color: '#2563eb' },
  assign: { bg: '#ede9fe', color: '#7c3aed' },
  resolve: { bg: '#dcfce7', color: '#16a34a' },
  sync: { bg: '#fef3c7', color: '#d97706' },
  update_permissions: { bg: '#ede9fe', color: '#7c3aed' },
  reset_permissions: { bg: '#fef3c7', color: '#d97706' },
  password_change: { bg: '#fef3c7', color: '#d97706' },
  password_reset: { bg: '#fee2e2', color: '#dc2626' },
};

function getActionStyle(action: string) {
  return ACTION_COLORS[action] || { bg: '#f1f5f9', color: '#64748b' };
}

function formatEntityType(type: string | null): string {
  if (!type) return '—';
  return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return formatDateTime(dateStr);
}

/* ══════════════════════════════════════════════
   Audit Log Table (shared between tabs)
   ══════════════════════════════════════════════ */
function AuditLogTable({
  preFilterEntity,
  preFilterAction,
}: {
  preFilterEntity?: string;
  preFilterAction?: string;
}) {
  const {
    logs, total, page, pages, isLoading, filters,
    setPage, setEntityType, setAction, setSearch,
    setDateRange, entityType, action, search, startDate, endDate,
  } = useAuditLogs(preFilterEntity, preFilterAction);

  const [expandedId, setExpandedId] = useState<number | null>(null);

  return (
    <div className="sa-audit-section">
      {/* Filter bar */}
      <div className="sa-audit-filters">
        {!preFilterEntity && (
          <select
            className="sa-filter-select"
            value={entityType}
            onChange={e => setEntityType(e.target.value)}
          >
            <option value="">All Entity Types</option>
            {filters?.entity_types.map(t => (
              <option key={t} value={t}>{formatEntityType(t)}</option>
            ))}
          </select>
        )}
        {!preFilterAction && (
          <select
            className="sa-filter-select"
            value={action}
            onChange={e => setAction(e.target.value)}
          >
            <option value="">All Actions</option>
            {filters?.actions.map(a => (
              <option key={a} value={a}>{a.replace(/_/g, ' ')}</option>
            ))}
          </select>
        )}
        <div className="sa-search-wrap">
          <Icons.Search className="sa-search-icon" />
          <input
            type="text"
            className="sa-filter-search"
            placeholder="Search user or action..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <input
          type="date"
          className="sa-filter-date"
          value={startDate}
          onChange={e => setDateRange(e.target.value, endDate)}
        />
        <input
          type="date"
          className="sa-filter-date"
          value={endDate}
          onChange={e => setDateRange(startDate, e.target.value)}
        />
      </div>

      {/* Table */}
      <div className="sa-audit-table-wrap">
        {isLoading ? (
          <div className="sa-loading"><div className="sa-spinner" /></div>
        ) : logs.length === 0 ? (
          <div className="sa-empty">
            <Icons.FileText />
            <p>No audit logs found</p>
          </div>
        ) : (
          <table className="sa-audit-table">
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>User</th>
                <th>Action</th>
                <th>Entity</th>
                <th>Entity ID</th>
                <th>IP Address</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {logs.map(log => {
                const style = getActionStyle(log.action);
                const isExpanded = expandedId === log.id;
                return (
                  <React.Fragment key={log.id}>
                    <tr className={`sa-audit-row ${isExpanded ? 'expanded' : ''}`}>
                      <td className="sa-audit-time">{formatDateTime(log.created_at || '')}</td>
                      <td>{log.user_name || '—'}</td>
                      <td>
                        <span className="sa-action-badge" style={{ backgroundColor: style.bg, color: style.color }}>
                          {log.action.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td>{formatEntityType(log.entity_type)}</td>
                      <td>{log.entity_id ?? '—'}</td>
                      <td className="sa-audit-ip">{log.ip_address || '—'}</td>
                      <td>
                        {(log.old_values || log.new_values) && (
                          <button
                            className="sa-expand-btn"
                            onClick={() => setExpandedId(isExpanded ? null : log.id)}
                            title={isExpanded ? 'Collapse' : 'View details'}
                          >
                            {isExpanded ? <Icons.ChevronDown /> : <Icons.ChevronRight />}
                          </button>
                        )}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="sa-audit-detail-row">
                        <td colSpan={7}>
                          <DetailPanel log={log} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div className="sa-pagination">
          <span className="sa-page-info">Showing {logs.length} of {total} entries</span>
          <div className="sa-page-controls">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)}>
              <Icons.ChevronLeft />
            </button>
            {Array.from({ length: Math.min(pages, 5) }, (_, i) => {
              let p: number;
              if (pages <= 5) {
                p = i + 1;
              } else if (page <= 3) {
                p = i + 1;
              } else if (page >= pages - 2) {
                p = pages - 4 + i;
              } else {
                p = page - 2 + i;
              }
              return (
                <button key={p} className={p === page ? 'active' : ''} onClick={() => setPage(p)}>
                  {p}
                </button>
              );
            })}
            <button disabled={page >= pages} onClick={() => setPage(page + 1)}>
              <Icons.ChevronRight />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailPanel({ log }: { log: AuditLogEntry }) {
  return (
    <div className="sa-detail-panel">
      {log.old_values && (
        <div className="sa-detail-block">
          <span className="sa-detail-label">Previous Values</span>
          <pre className="sa-detail-json">{JSON.stringify(log.old_values, null, 2)}</pre>
        </div>
      )}
      {log.new_values && (
        <div className="sa-detail-block">
          <span className="sa-detail-label">New Values</span>
          <pre className="sa-detail-json">{JSON.stringify(log.new_values, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════
   Main Dashboard Component
   ══════════════════════════════════════════════ */
export function SuperAdminDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data, isLoading, error, refetch } = useSuperAdminDashboard();
  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  const tabRef = useRef(setActiveTab);
  tabRef.current = setActiveTab;

  if (error) {
    return (
      <div className="dashboard-error">
        <Icons.AlertCircle />
        <h2>Error loading dashboard</h2>
        <p>{error}</p>
        <button onClick={refetch}>Try Again</button>
      </div>
    );
  }

  if (!data || !user) {
    return (
      <div className="super-admin-dashboard">
        <header className="dashboard-header-content">
          <div className="header-greeting">
            <h1>{getGreeting()}, {user?.name ? getFirstName(user.name) : 'Admin'}!</h1>
            <p>Loading dashboard...</p>
          </div>
        </header>
        {isLoading && <div className="sa-loading"><div className="sa-spinner" /></div>}
      </div>
    );
  }

  const tabs: { key: TabKey; label: string; icon: React.ReactNode }[] = [
    { key: 'overview', label: 'Overview', icon: <Icons.Dashboard /> },
    { key: 'activity', label: 'Activity Log', icon: <Icons.Activity /> },
    { key: 'settings', label: 'Settings Log', icon: <Icons.Settings /> },
    { key: 'permissions', label: 'Permissions Log', icon: <Icons.Shield /> },
  ];

  const statCards = [
    { title: 'Total Users', value: data.totalUsers, icon: <Icons.Users />, color: '#3b82f6', link: '/employees' },
    { title: 'Active Projects', value: data.activeProjects ?? data.totalProjects, icon: <Icons.Briefcase />, color: '#22c55e', link: '/projects' },
    { title: 'Hours This Week', value: data.totalHoursLogged.toLocaleString(undefined, { maximumFractionDigits: 0 }), icon: <Icons.Clock />, color: '#f59e0b', link: '/timesheets' },
    { title: 'Pending Approvals', value: data.pendingApprovals, icon: <Icons.FileText />, color: '#ef4444', link: '/approvals' },
  ];

  const quickActions = [
    { label: 'Add New User', icon: <Icons.Plus />, color: '#3b82f6', path: '/employees' },
    { label: 'Create Project', icon: <Icons.Briefcase />, color: '#22c55e', path: '/projects' },
    { label: 'View Approvals', icon: <Icons.Check />, color: '#8b5cf6', path: '/approvals' },
    { label: 'System Settings', icon: <Icons.Settings />, color: '#64748b', path: '/settings' },
  ];

  return (
    <div className="super-admin-dashboard">
      {/* Header */}
      <header className="dashboard-welcome">
        <div className="welcome-text">
          <h1>{getGreeting()}, {getFirstName(user.name)}!</h1>
          <p>Here's your organization overview for today</p>
        </div>
        <div className="welcome-date">
          <span className="date-text">
            {new Date().toLocaleDateString('en-US', {
              weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
            })}
          </span>
        </div>
      </header>

      {/* Stat Cards — always visible */}
      <section className="stats-grid">
        {statCards.map((stat, index) => (
          <Card
            key={index}
            className="stat-card"
            padding="none"
            hoverable
            onClick={() => navigate(stat.link)}
          >
            <div className="stat-card-inner">
              <div className="stat-icon-wrapper" style={{ backgroundColor: `${stat.color}15`, color: stat.color }}>
                {stat.icon}
              </div>
              <div className="stat-details">
                <span className="stat-title">{stat.title}</span>
                <span className="stat-value">{stat.value}</span>
              </div>
            </div>
          </Card>
        ))}
      </section>

      {/* Tabs */}
      <div className="sa-tabs">
        {tabs.map(tab => (
          <button
            key={tab.key}
            className={`sa-tab ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.icon}
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <OverviewTab
          data={data}
          navigate={navigate}
          quickActions={quickActions}
          onViewAllActivity={() => setActiveTab('activity')}
        />
      )}
      {activeTab === 'activity' && (
        <Card className="sa-log-card">
          <div className="card-header-row">
            <h3 className="card-title">All Activity Logs</h3>
            <span className="sa-log-subtitle">Complete audit trail of all system actions</span>
          </div>
          <AuditLogTable />
        </Card>
      )}
      {activeTab === 'settings' && (
        <Card className="sa-log-card">
          <div className="card-header-row">
            <h3 className="card-title">Settings Change Log</h3>
            <span className="sa-log-subtitle">All system settings modifications</span>
          </div>
          <AuditLogTable preFilterEntity="system_setting" />
        </Card>
      )}
      {activeTab === 'permissions' && (
        <Card className="sa-log-card">
          <div className="card-header-row">
            <h3 className="card-title">Permissions Change Log</h3>
            <span className="sa-log-subtitle">Role and permission modifications</span>
          </div>
          <AuditLogTable preFilterAction="update_permissions" />
        </Card>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════
   Overview Tab
   ══════════════════════════════════════════════ */
function OverviewTab({
  data, navigate, quickActions, onViewAllActivity,
}: {
  data: NonNullable<ReturnType<typeof useSuperAdminDashboard>['data']>;
  navigate: ReturnType<typeof useNavigate>;
  quickActions: Array<{ label: string; icon: React.ReactNode; color: string; path: string }>;
  onViewAllActivity: () => void;
}) {
  return (
    <>
      {/* Main content grid */}
      <div className="dashboard-content-grid">
        <div className="main-column">
          {/* Recent Activity */}
          <Card className="activity-card">
            <div className="card-header-row">
              <h3 className="card-title">Recent Activity</h3>
              <button className="view-all-link" onClick={onViewAllActivity}>View All</button>
            </div>
            <div className="activity-list">
              {data.recentActivity.length === 0 ? (
                <div className="sa-empty compact">
                  <Icons.Activity />
                  <p>No recent activity</p>
                </div>
              ) : (
                data.recentActivity.slice(0, 10).map((activity) => {
                  const actKey = activity.type === 'user_created' ? 'create'
                    : activity.type === 'timesheet_approved' ? 'approve'
                    : activity.type === 'project_created' ? 'create' : 'update';
                  const style = getActionStyle(actKey);
                  return (
                    <div key={activity.id} className="activity-item">
                      <div className="activity-dot" style={{ backgroundColor: style.color }}></div>
                      <div className="activity-info">
                        <p className="activity-message">{activity.message}</p>
                        <span className="activity-time">{timeAgo(activity.timestamp)}</span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </Card>

          {/* Recent Settings Changes */}
          {data.settingsChanges && data.settingsChanges.length > 0 && (
            <Card className="sa-recent-log-card">
              <div className="card-header-row">
                <h3 className="card-title">
                  <Icons.Settings className="sa-title-icon" /> Recent Settings Changes
                </h3>
              </div>
              <div className="sa-mini-log-list">
                {data.settingsChanges.map(log => (
                  <div key={log.id} className="sa-mini-log-item">
                    <div className="sa-mini-log-info">
                      <span className="sa-mini-log-user">{log.user_name || 'System'}</span>
                      <span className="sa-mini-log-action">{log.action.replace(/_/g, ' ')}</span>
                    </div>
                    <span className="sa-mini-log-time">{timeAgo(log.created_at)}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Recent Permissions Changes */}
          {data.permissionsChanges && data.permissionsChanges.length > 0 && (
            <Card className="sa-recent-log-card">
              <div className="card-header-row">
                <h3 className="card-title">
                  <Icons.Shield className="sa-title-icon" /> Recent Permissions Changes
                </h3>
              </div>
              <div className="sa-mini-log-list">
                {data.permissionsChanges.map(log => (
                  <div key={log.id} className="sa-mini-log-item">
                    <div className="sa-mini-log-info">
                      <span className="sa-mini-log-user">{log.user_name || 'System'}</span>
                      <span className="sa-mini-log-action">{log.action.replace(/_/g, ' ')}</span>
                    </div>
                    <span className="sa-mini-log-time">{timeAgo(log.created_at)}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>

        <div className="side-column">
          {/* Quick Actions */}
          <Card className="quick-actions-card">
            <h3 className="card-title">Quick Actions</h3>
            <div className="quick-actions-grid">
              {quickActions.map((act, index) => (
                <button key={index} className="quick-action-btn" onClick={() => navigate(act.path)}>
                  <span className="action-icon" style={{ color: act.color }}>{act.icon}</span>
                  <span className="action-label">{act.label}</span>
                </button>
              ))}
            </div>
          </Card>

          {/* System Health */}
          <Card className="system-health-card">
            <div className="card-header-row">
              <h3 className="card-title">System Health</h3>
              <span className={`health-badge ${data.systemHealth.status}`}>
                {data.systemHealth.status}
              </span>
            </div>
            <div className="health-metrics">
              <div className="health-metric">
                <Icons.Activity className="metric-icon" />
                <div className="metric-info">
                  <span className="metric-label">Uptime</span>
                  <span className="metric-value">{data.systemHealth.uptime}</span>
                </div>
              </div>
              <div className="health-metric">
                <Icons.Users className="metric-icon" />
                <div className="metric-info">
                  <span className="metric-label">Active Users (24h)</span>
                  <span className="metric-value">{data.systemHealth.activeUsers}</span>
                </div>
              </div>
              <div className="health-metric">
                <Icons.Clock className="metric-icon" />
                <div className="metric-info">
                  <span className="metric-label">Last Sync</span>
                  <span className="metric-value">{timeAgo(data.systemHealth.lastBackup)}</span>
                </div>
              </div>
              <div className="health-metric">
                <Icons.Chart className="metric-icon" />
                <div className="metric-info">
                  <span className="metric-label">All-Time Hours</span>
                  <span className="metric-value">
                    {(data.totalHoursAllTime ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </span>
                </div>
              </div>
            </div>
          </Card>

          {/* Users by Role */}
          <Card className="roles-distribution-card">
            <h3 className="card-title">Users by Role</h3>
            <div className="roles-list">
              {data.usersByRole.map((item) => {
                const total = data.usersByRole.reduce((sum, r) => sum + r.count, 0);
                const percentage = total > 0 ? Math.round((item.count / total) * 100) : 0;
                return (
                  <div key={item.role} className="role-item">
                    <div className="role-header">
                      <span className="role-name">{ROLE_FULL_NAMES[item.role] || item.role}</span>
                      <span className="role-count">{item.count}</span>
                    </div>
                    <div className="role-bar">
                      <div className="role-bar-fill" style={{ width: `${percentage}%` }}></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}

export default SuperAdminDashboard;
